import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { SavingStatus } from '@/api/notes/useSaveNote';
import type { Note } from '@/store/notesStore';

interface NoteContextValue {
  savingStatus: SavingStatus;
  noteId: string | null;
  hasKeyWrapper: boolean;
  setHasKeyWrapper: (value: boolean) => void;
  isVersionsPanelOpen: boolean;
  onToggleVersionsPanel: () => void;
  isReadOnly: boolean;
  viewingVersion: number | null;
  onBackToLatest: () => void;
  note: Note | null;
  currentContent: string;
  saveCurrent: () => Promise<boolean>;
  getSeq: () => number;
  onEncryptionComplete: (encrypted: boolean, seq: number, body: string) => void;
  isEncryptionConversionActive: boolean;
  setEncryptionConversionActive: (active: boolean) => void;
}

const NoteContext = createContext<NoteContextValue | undefined>(undefined);

interface NoteProviderProps {
  children: ReactNode;
  savingStatus: SavingStatus;
  noteId: string | null;
  initialHasKeyWrapper?: boolean;
  isVersionsPanelOpen?: boolean;
  onToggleVersionsPanel?: () => void;
  isReadOnly?: boolean;
  viewingVersion?: number | null;
  onBackToLatest?: () => void;
  note?: Note | null;
  currentContent?: string;
  saveCurrent?: () => Promise<boolean>;
  getSeq?: () => number;
  onEncryptionComplete?: (encrypted: boolean, seq: number, body: string) => void;
  isEncryptionConversionActive?: boolean;
  onEncryptionConversionActiveChange?: (active: boolean) => void;
}

export const NoteProvider: React.FC<NoteProviderProps> = ({
  children,
  savingStatus,
  noteId,
  initialHasKeyWrapper = false,
  isVersionsPanelOpen = false,
  onToggleVersionsPanel = () => {},
  isReadOnly = false,
  viewingVersion = null,
  onBackToLatest = () => {},
  note = null,
  currentContent = '',
  saveCurrent = async () => true,
  getSeq = () => 0,
  onEncryptionComplete = () => {},
  isEncryptionConversionActive: conversionActive = false,
  onEncryptionConversionActiveChange = () => {},
}) => {
  const [hasKeyWrapper, setHasKeyWrapper] = useState(initialHasKeyWrapper);

  return (
    <NoteContext.Provider value={{
      savingStatus, noteId, hasKeyWrapper, setHasKeyWrapper,
      isVersionsPanelOpen, onToggleVersionsPanel,
      isReadOnly: isReadOnly || conversionActive, viewingVersion, onBackToLatest,
      note, currentContent, saveCurrent, getSeq, onEncryptionComplete,
      isEncryptionConversionActive: conversionActive, setEncryptionConversionActive: onEncryptionConversionActiveChange,
    }}>
      {children}
    </NoteContext.Provider>
  );
};

export const useNoteContext = (): NoteContextValue => {
  const context = useContext(NoteContext);
  if (!context) {
    throw new Error('useNoteContext must be used within a NoteProvider');
  }
  return context;
};

export type { SavingStatus };
